export interface CalltouchParams {
  sessionId?: string | number;
}

export type CalltouchDiagnosticStatus =
  | 'success'
  | 'cached'
  | 'disabled'
  | 'invalid_mod_id'
  | 'loader_error'
  | 'script_unavailable'
  | 'loading_timeout'
  | 'empty_session'
  | 'invalid_session'
  | 'exception';

export type CalltouchLoaderStatus = 'not_started' | 'loading' | 'loaded' | 'error';
export type CalltouchSessionSource = 'live' | 'session_storage' | 'calltouch_cookie' | 'none';

export interface CalltouchFunction {
  (...args: unknown[]): CalltouchParams | undefined;
  loaded?: boolean;
}

export interface CalltouchDiagnostics {
  status: CalltouchDiagnosticStatus;
  source: CalltouchSessionSource;
  waited_ms: number;
  loader_status: CalltouchLoaderStatus;
  loader_attempts: number;
  ct_present: boolean;
  ct_loaded: boolean | null;
}

export interface CalltouchSessionResult {
  sessionId: string | null;
  diagnostics: CalltouchDiagnostics;
}

export const CALLTOUCH_SESSION_STORAGE_KEY = 'wtfish:calltouch:session';
export const CALLTOUCH_MOD_ID_PATTERN = /^[A-Za-z0-9_-]+$/;

export function buildCalltouchLoaderScript(modId: string): string {
  return `(function(w,d,n,c){var state=w.__wtfishCalltouchLoader={status:"not_started",attempts:0,retryScheduled:false,retry:function(){if(state.attempts>=2||state.status==="loaded"){return}p(c)}};w.CalltouchDataObject=n;w[n]=function(){w[n]["callbacks"].push(arguments)};if(!w[n]["callbacks"]){w[n]["callbacks"]=[]}w[n]["loaded"]=false;w[n]["counters"]=[c];function p(cId){state.attempts+=1;state.status="loading";var a=d.getElementsByTagName("script")[0],s=d.createElement("script"),i=function(){a.parentNode.insertBefore(s,a)},m=typeof Array.prototype.find === "function",f=m?"init-min.js":"init.js";s.async=true;s.src="https://mod.calltouch.ru/"+f+"?id="+cId;s.onload=function(){state.status="loaded"};s.onerror=function(){state.status="error";if(state.attempts<2&&!state.retryScheduled){state.retryScheduled=true;setTimeout(function(){state.retryScheduled=false;state.retry()},500)}};if(w.opera==="[object Opera]"){d.addEventListener("DOMContentLoaded",i,false)}else{i()}}p(c)})(window,document,"ct",${JSON.stringify(modId)});`;
}

function makeDiagnostics(
  status: CalltouchDiagnosticStatus,
  ct: CalltouchFunction | undefined,
  loaderStatus: CalltouchLoaderStatus,
  waitedMs: number,
  source: CalltouchSessionSource = 'none',
  loaderAttempts = 0,
): CalltouchDiagnostics {
  return {
    status,
    source,
    waited_ms: Math.max(0, Math.round(waitedMs)),
    loader_status: loaderStatus,
    loader_attempts: Math.max(0, Math.round(loaderAttempts)),
    ct_present: Boolean(ct),
    ct_loaded: ct?.loaded === undefined ? null : ct.loaded,
  };
}

export function inspectCalltouchSession(
  ct: CalltouchFunction | undefined,
  modId: string,
  loaderStatus: CalltouchLoaderStatus = 'not_started',
  waitedMs = 0,
  loaderAttempts = 0,
): CalltouchSessionResult {
  if (loaderStatus === 'error') {
    return {
      sessionId: null,
      diagnostics: makeDiagnostics('loader_error', ct, loaderStatus, waitedMs, 'none', loaderAttempts),
    };
  }

  if (!ct) {
    return {
      sessionId: null,
      diagnostics: makeDiagnostics(
        loaderStatus === 'loading' ? 'loading_timeout' : 'script_unavailable',
        ct,
        loaderStatus,
        waitedMs,
        'none',
        loaderAttempts,
      ),
    };
  }

  if (ct.loaded === false) {
    return {
      sessionId: null,
      diagnostics: makeDiagnostics('loading_timeout', ct, loaderStatus, waitedMs, 'none', loaderAttempts),
    };
  }

  try {
    const sessionId = ct('calltracking_params', modId)?.sessionId;

    if (sessionId === undefined || sessionId === null || sessionId === '') {
      return {
        sessionId: null,
        diagnostics: makeDiagnostics('empty_session', ct, loaderStatus, waitedMs, 'none', loaderAttempts),
      };
    }

    const normalizedSessionId = String(sessionId);
    if (!/^\d+$/.test(normalizedSessionId)) {
      return {
        sessionId: null,
        diagnostics: makeDiagnostics('invalid_session', ct, loaderStatus, waitedMs, 'none', loaderAttempts),
      };
    }

    return {
      sessionId: normalizedSessionId,
      diagnostics: makeDiagnostics('success', ct, loaderStatus, waitedMs, 'live', loaderAttempts),
    };
  } catch {
    return {
      sessionId: null,
      diagnostics: makeDiagnostics('exception', ct, loaderStatus, waitedMs, 'none', loaderAttempts),
    };
  }
}

export function readCalltouchSessionId(ct: CalltouchFunction | undefined, modId: string): string | null {
  return inspectCalltouchSession(ct, modId).sessionId;
}

export function readCalltouchCookieSessionId(cookie: string): string | null {
  const encodedValue = cookie
    .split(';')
    .map((part) => part.trim())
    .find((part) => part.startsWith('_ct_session_id='))
    ?.slice('_ct_session_id='.length);

  if (!encodedValue) return null;

  try {
    const sessionId = decodeURIComponent(encodedValue);
    return /^\d+$/.test(sessionId) ? sessionId : null;
  } catch {
    return null;
  }
}

export async function waitForCalltouchSession(
  ctProvider: () => CalltouchFunction | undefined,
  modId: string,
  loaderStatusProvider: () => CalltouchLoaderStatus = () => 'not_started',
  timeoutMs = 1000,
  pollIntervalMs = 50,
  loaderAttemptsProvider: () => number = () => 0,
): Promise<CalltouchSessionResult> {
  const startedAt = Date.now();
  const deadline = startedAt + timeoutMs;
  let result = inspectCalltouchSession(
    ctProvider(),
    modId,
    loaderStatusProvider(),
    0,
    loaderAttemptsProvider(),
  );

  while (Date.now() < deadline) {
    result = inspectCalltouchSession(
      ctProvider(),
      modId,
      loaderStatusProvider(),
      Date.now() - startedAt,
      loaderAttemptsProvider(),
    );
    if (result.sessionId || result.diagnostics.status === 'exception' || result.diagnostics.status === 'invalid_session') {
      return result;
    }

    await new Promise((resolve) => setTimeout(resolve, pollIntervalMs));
  }

  return inspectCalltouchSession(
    ctProvider(),
    modId,
    loaderStatusProvider(),
    Date.now() - startedAt,
    loaderAttemptsProvider(),
  );
}

export async function waitForCalltouchSessionId(
  ctProvider: () => CalltouchFunction | undefined,
  modId: string,
  timeoutMs = 1000,
  pollIntervalMs = 50,
): Promise<string | null> {
  return (await waitForCalltouchSession(ctProvider, modId, () => 'not_started', timeoutMs, pollIntervalMs)).sessionId;
}
